import { ChangeEvent, FocusEvent, FormEvent, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { FaPaperclip, FaPaperPlane, FaRegCommentAlt, FaSmileWink, FaWhatsappSquare } from "react-icons/fa";
import { StyledTypeMessageForm } from "./style";
import { ChatContext } from "../../../contexts/user/ChatContext";
import { IoMdClose } from "react-icons/io";
import { CustomState } from "../../../Hooks/State.hook";
import { useAudioRecorder, AudioRecorder } from "react-audio-voice-recorder";
import { AttendancesContext } from "../../../contexts/user/AttendancesContext";
import { QuickMessage } from "../../QuickMessage";
import { Modal } from "../../../styles/modal";
import { QuickTemplate } from "../../QuickTemplate";
import { SocketContext } from "../../../contexts/global/SocketContext";
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { SpecificAttendanceDetails } from "../../../interfaces/Attendances.interfaces";
import { LoginContext } from "../../../contexts/global/LoginContext";
import { StyledViewFileButton } from "../Message/style";
import { baseURL } from "../../../services/api";
import { decodeSafeURI } from "../../../Hooks/decodeSafeUri.hook";
import { FileIcon, defaultStyles } from 'react-file-icon';

const whatsappLimits: { [key: string]: number } = {
    'image/': 16,
    'video/': 64,
    'audio/': 16,
    'application/': 100,
    'text/': 100,
    'default': 64
};
interface Props {
    attendance: SpecificAttendanceDetails;
}

export const TypeMessageForm = ({ attendance }: Props) => {
    const enabled = CustomState<boolean>(true);

    const [rows, setRows] = useState(1);
    const [isShiftPressed, setIsShiftPressed] = useState(false);

    const {
        sendMessage,
        referenceId,
        replyingMessage,
        handleFileSelect,
        formData
    } = useContext(ChatContext);

    const { modalState, attendancesMessages, attendancesMessagesGrouped } = useContext(AttendancesContext);
    const { oficialMode } = useContext(SocketContext);
    const { darkMode } = useContext(LoginContext);

    const referenceFileComponent = CustomState<ReactNode>("");
    const referenceViewComponent = CustomState<boolean>(false);
    const [fileWarning, setFileWarning] = useState<string | null>(null);

    const referenceMessage = replyingMessage.value;

    const referenceFileType = (referenceMessage && referenceMessage.ARQUIVO) && (referenceMessage.ARQUIVO.TIPO.toUpperCase().includes("IMAGE") ? "imagem" :
        referenceMessage.ARQUIVO && referenceMessage.ARQUIVO.TIPO.toUpperCase().includes("VIDEO") ? "video" :
            referenceMessage.ARQUIVO && referenceMessage.ARQUIVO.TIPO.toUpperCase().includes("PTT") ? "audio" :
                referenceMessage.ARQUIVO && referenceMessage.ARQUIVO.TIPO.toUpperCase().includes("AUDIO") ? "audio" :
                    "documento");

    useEffect(() => {
        if (referenceMessage && referenceMessage.ARQUIVO) {
            const type = referenceMessage.ARQUIVO.TIPO.toUpperCase();
            const fileURL = `${baseURL}/custom-routes/file/${decodeSafeURI(referenceMessage.ARQUIVO.NOME_ARQUIVO)}` || "";

            if (type.includes("IMAGE")) {
                referenceFileComponent.set(<img src={fileURL} alt="" />)
            } else if (type.includes("VIDEO")) {
                referenceFileComponent.set(<video controls>
                    <source src={fileURL} type={referenceMessage.ARQUIVO.TIPO} />
                </video>);
            } else if (type.includes("PTT") || type.includes("AUDIO")) {
                referenceFileComponent.set(<audio controls>
                    <source src={fileURL} type={referenceMessage.ARQUIVO.TIPO} />
                </audio>);
            } else {
                const ext = referenceMessage.ARQUIVO?.NOME_ARQUIVO.split(".").reverse()[0];

                referenceFileComponent.set(<a href={fileURL} target="_blank" rel="noreferrer">
                    <FileIcon extension={ext} {...(defaultStyles as any)[ext]} />
                    <div>
                        <p> {decodeSafeURI(referenceMessage.ARQUIVO.NOME_ORIGINAL || referenceMessage.ARQUIVO.NOME_ARQUIVO)} </p>
                        <p> tipo: {referenceMessage.ARQUIVO.TIPO} </p>
                    </div>
                </a>);
            };
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceMessage]);

    const openQuickMessages = () => {
        if (oficialMode.value) {
            modalState.set(<Modal><QuickMessage attendance={attendance} /></Modal>);
        } else {
            modalState.set(<Modal><QuickMessage attendance={attendance} /></Modal>);
        };
    };
    const openQuickTemplate = () => modalState.set(<Modal><QuickTemplate attendance={attendance} /></Modal>)

    function onChangeFunction(e: ChangeEvent<HTMLTextAreaElement>) {
        formData.set((prev) => {
            return { ...prev, text: e.target.value };
        });

        setRows(e.target.value.split(/\n/g).length);
    };

    function onFocusFunction(e: FocusEvent<HTMLTextAreaElement>) {
        setRows(e.target.value.split(/\n/g).length);
    };

    function onBlurFunction(e: FocusEvent<HTMLTextAreaElement>) {
        let totalRows = e.target.value.split(/\n/g).length;
        setRows(1);
        setTimeout(() => {
            e.target.scroll(0, totalRows * 16)
        }, 50);
    };

    function onSubmitFunction(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        sendMessage(attendance);
        formData.reset();
        referenceId.reset()
    };

    const recorderControls = useAudioRecorder();

    const addAudioElement = (blob: Blob) => {
        const file = new File([blob], "audio.webm", { type: blob.type });

        formData.set((prev) => {
            return { text: "", isAudio: true, file, referenceId: prev.referenceId };
        });
    };

    const onAudioClose = () => {
        formData.set((prev) => {
            return { ...prev, file: null, isAudio: false };
        });
    };

    useEffect(() => {
        if (!recorderControls.isRecording) {
            enabled.set(false);
            setTimeout(() => {
                enabled.set(true);
            }, 1000);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recorderControls.isRecording]);

    const inputFileRef = useRef<HTMLInputElement>(null);

    const [isDisabled, setDisabled] = useState(false);

    function onKeyDownFunction(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !isShiftPressed) {
            e.preventDefault();

            if (formData.value.text.trim() !== '') {
                sendMessage(attendance);
                formData.reset();
            }
        } else if (e.key === 'Shift') {
            setIsShiftPressed(true);
        }
    }

    function onKeyUpFunction(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Shift') {
            setIsShiftPressed(false);
        }
    }


    useEffect(() => {
        const contactId = attendance.CODIGO_NUMERO;
        const messagesGrouped = attendancesMessagesGrouped.value[contactId] ? attendancesMessagesGrouped.value[contactId] : []
        const contactMessage = messagesGrouped.find(m => m.CODIGO_NUMERO === contactId && !m.FROM_ME);

        if (oficialMode.value && !contactMessage) {
            setDisabled(true);
        } else {
            setDisabled(false);
        };
    }, [attendance.CODIGO_NUMERO, oficialMode.value, attendancesMessagesGrouped.value]);

    const [isEmojiActive, setEmojiActive] = useState(false);

    return (
         <>
        {fileWarning && (
            <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    background: 'white',
                    padding: '1rem 2rem',
                    borderRadius: '8px',
                    maxWidth: '90%',
                    textAlign: 'center'
                }}>
                    <p>{fileWarning}</p>
                    <button onClick={() => setFileWarning(null)}>Entendi</button>
                </div>
            </div>
        )}
        <StyledTypeMessageForm onSubmit={onSubmitFunction} >
            {
                replyingMessage.value &&
                <div className="replyingMessage">
                    <div>
                        <h2> {replyingMessage.value.FROM_ME ? "Você" : attendance.CONTATO_NOME} </h2>
                        <div className="file">
                            {
                                ["imagem", "video", "audio"].includes(referenceFileType || "") ?
                                    (
                                        referenceViewComponent.value ?
                                            referenceFileComponent.value
                                            :
                                            <StyledViewFileButton onClick={() => referenceViewComponent.set(prev => !prev)} className="view_file">
                                                Carregar {referenceFileType}
                                            </StyledViewFileButton>
                                    )
                                    :
                                    (
                                        referenceFileComponent.value
                                    )
                            }
                        </div>
                        <span> {decodeURI(replyingMessage.value.MENSAGEM).split(/\n/g).map(p => <p> {p}</p>)} </span>
                    </div>
                    <IoMdClose onClick={() => referenceId.reset()} />
                </div>
            }
            {
                isEmojiActive &&
                <div className="emoji_picker">
                    <EmojiPicker
                        onEmojiClick={(emoji, event) => {
                            formData.set(prev => ({ ...prev, text: prev.text + emoji.emoji }))
                        }}
                        theme={darkMode ? Theme.DARK : Theme.LIGHT}
                    />
                </div>
            }
            <div className="main">
                <div>
                    <button
                        type="button"
                        onClick={openQuickMessages}
                        disabled={isDisabled}
                        title="Mensagens prontas"
                    >
                        <FaRegCommentAlt />
                    </button>
                    {
                        oficialMode.value &&
                        <button
                            type="button"
                            onClick={openQuickTemplate}
                            title="Templates Whatsapp"
                        >
                            <FaWhatsappSquare />
                        </button>

                    }
                        <input
                        type="file"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            console.log(file);
                            const type = file.type;
                            const matchedType = Object.keys(whatsappLimits).find(key => type.startsWith(key)) || 'default';
                            const limit = whatsappLimits[matchedType];
                            const sizeInMB = file.size / (1024 * 1024);

                            if (sizeInMB > limit) {
                            setFileWarning(`O arquivo possui ${sizeInMB.toFixed(2)}MB e excede o limite do WhatsApp para "o tipo ${type}". Ele será enviado como link.`);
                            }

                            // continua normalmente
                            handleFileSelect(e);
                        }}
                        style={{ display: 'none' }}
                        ref={inputFileRef}
                        />

                    <button
                        onClick={() => inputFileRef.current?.click()}
                        type="button"
                        disabled={isDisabled}
                        title="Anexar arquivo"
                    >
                        <FaPaperclip />
                    </button>
                    <button
                        onClick={() => setEmojiActive(prev => !prev)}
                        type="button"
                    >
                        <FaSmileWink />
                    </button>
                </div>
                {
                    !!formData.value.file && formData.value.isAudio ?
                        <>
                            <audio controls src={URL.createObjectURL(formData.value.file)} />
                            <IoMdClose onClick={onAudioClose} />
                        </>
                        :
                        <textarea
                            placeholder="Mensagem"
                            onBlur={onBlurFunction}
                            onFocus={onFocusFunction}
                            onChange={onChangeFunction}
                            onKeyDown={onKeyDownFunction}
                            onKeyUp={onKeyUpFunction}
                            value={formData.value.text}
                            rows={rows}
                            disabled={isDisabled}
                        />
                }
                <div>
                    {
                        !isDisabled &&
                        <AudioRecorder
                            onRecordingComplete={(blob) => addAudioElement(blob)}
                            recorderControls={recorderControls}
                            audioTrackConstraints={{
                                noiseSuppression: true,
                                echoCancellation: true,
                            }}
                        />
                    }



                    <button
                        type="submit"
                        disabled={(recorderControls.isRecording || !enabled.value) || (formData.value.text.length === 0 && !formData.value.file) || isDisabled}
                        title="Enviar mensagem"
                    >
                        <FaPaperPlane />
                    </button>
                </div>
            </div>
        </StyledTypeMessageForm>
          </>

    );
};